import type { FC } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";

import { type AccountDropdownSettings } from "../../Header";
import { SignOut } from "../SignOut/SignOut";

import { getAccountLinks } from "./utils/getProfileDropdownLink";

import styles from "./UserActions.module.scss";

type UserDialogProps = {
  setIsUserDialogOpen: (isOpen: boolean) => void;
  accountDropdownSettings?: AccountDropdownSettings;
  userName?: string;
  onLogOut: () => void;
  isEnabledUserIndicationWithName: boolean;
};

export const UserDialog: FC<UserDialogProps> = ({
  setIsUserDialogOpen,
  userName,
  onLogOut,
  accountDropdownSettings,
  isEnabledUserIndicationWithName,
}) => {
  const { __ } = useTranslations();

  if (!accountDropdownSettings) {
    return null;
  }

  const { logoutLabel } = accountDropdownSettings;

  const handleLinkClick = () => {
    setIsUserDialogOpen(false);
  };

  const userDialogLinks = getAccountLinks(accountDropdownSettings);

  return (
    <div
      className={classNames(styles.userDialog, {
        [styles.userDialogRight]: isEnabledUserIndicationWithName,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      {userName && accountDropdownSettings.greetingMessage && (
        <p className={styles.dialogTitle}>
          {accountDropdownSettings.greetingMessage}
          {!accountDropdownSettings.hideGreetingMessage && (
            <>
              {" "}
              <span>{userName}</span>
            </>
          )}
        </p>
      )}
      <button className={styles.dialogClose} onClick={() => setIsUserDialogOpen(false)} aria-label={__("common.close")}>
        <UiCrossIcon aria-hidden="true" />
      </button>
      <ul>
        {userDialogLinks.map(({ href, label, nativeBehaviour }) => (
          <li className={styles.dialogItem} key={`${label}-${href}`}>
            <LinkBehaviour
              onClick={handleLinkClick}
              className={styles.link}
              href={href}
              nativeBehaviour={nativeBehaviour}
            >
              {label}
            </LinkBehaviour>
          </li>
        ))}
      </ul>

      <SignOut label={logoutLabel ?? ""} onLogOut={onLogOut} />
    </div>
  );
};
